import React from 'react';
import { motion } from 'framer-motion';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, CalendarIcon, ChartBarIcon, LightBulbIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '../../../context/DarkModeContext';

interface SavedPreparationViewerProps {
  isOpen: boolean;
  onClose: () => void;
  preparationData: any;
}

const SavedPreparationViewer: React.FC<SavedPreparationViewerProps> = ({
  isOpen,
  onClose,
  preparationData
}) => {
  const { darkMode } = useDarkMode();
  
  if (!preparationData) return null;
  
  // Helper function to clean up escaped characters
  const cleanText = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/\\'/g, "'")
      .replace(/\\"/g, '"')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\\\/g, '\\');
  };
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const sectionBgColor = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  
  const completedDate = new Date(preparationData.completed_at).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className={`mx-auto max-w-3xl w-full ${bgColor} rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col`}>
          {/* Header */}
          <div className={`px-6 py-4 border-b ${borderColor} flex items-center justify-between`}>
            <Dialog.Title className={`text-lg font-semibold ${textColor}`}>
              Review Preparation Summary
            </Dialog.Title>
            <button
              onClick={onClose}
              className={`p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {/* Date */}
            <div className={`flex items-center gap-2 ${mutedTextColor}`}>
              <CalendarIcon className="w-5 h-5" />
              <span className="text-sm">Prepared on {completedDate}</span>
            </div>
            
            {/* Metrics */}
            {preparationData.metrics_snapshot && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`${sectionBgColor} rounded-lg p-4 border ${borderColor}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <ChartBarIcon className="w-5 h-5 text-blue-500" />
                  <h3 className={`font-semibold ${textColor}`}>Coverage Metrics</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-sm ${mutedTextColor}`}>Overall Coverage</p>
                    <p className={`text-2xl font-bold ${textColor}`}>
                      {preparationData.metrics_snapshot.overallCoverage || 0}%
                    </p>
                  </div>
                  <div>
                    <p className={`text-sm ${mutedTextColor}`}>Evidence Cards</p>
                    <p className={`text-2xl font-bold ${textColor}`}>
                      {preparationData.metrics_snapshot.totalCards || 0}
                    </p>
                  </div>
                  {preparationData.metrics_snapshot.criticalGaps?.length > 0 && (
                    <div className="col-span-2">
                      <p className={`text-sm ${mutedTextColor}`}>Critical Gaps</p>
                      <p className={`text-lg font-semibold text-orange-500`}>
                        {preparationData.metrics_snapshot.criticalGaps.length} areas need attention
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
            
            {/* Reflections */}
            {preparationData.reflections && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`${sectionBgColor} rounded-lg p-4 border ${borderColor}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <LightBulbIcon className="w-5 h-5 text-yellow-500" />
                  <h3 className={`font-semibold ${textColor}`}>Reflections</h3>
                </div>
                <div className="space-y-4">
                  {preparationData.reflections.biggestWin && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>🎯 Biggest Win</h4>
                      <p className={`text-sm ${mutedTextColor} whitespace-pre-wrap`}>
                        {cleanText(preparationData.reflections.biggestWin)}
                      </p>
                    </div>
                  )}
                  {preparationData.reflections.keyLearnings && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>📚 Key Learnings</h4>
                      <p className={`text-sm ${mutedTextColor} whitespace-pre-wrap`}>
                        {cleanText(preparationData.reflections.keyLearnings)}
                      </p>
                    </div>
                  )}
                  {preparationData.reflections.challenges && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>💪 Challenges Overcome</h4>
                      <p className={`text-sm ${mutedTextColor} whitespace-pre-wrap`}>
                        {cleanText(preparationData.reflections.challenges)}
                      </p>
                    </div>
                  )}
                  {preparationData.reflections.growth && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>🌱 Growth & Development</h4>
                      <p className={`text-sm ${mutedTextColor} whitespace-pre-wrap`}>
                        {cleanText(preparationData.reflections.growth)}
                      </p>
                    </div>
                  )}
                  {preparationData.reflections.proudestMoment && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>⭐ Proudest Moment</h4>
                      <p className={`text-sm ${mutedTextColor} whitespace-pre-wrap`}>
                        {cleanText(preparationData.reflections.proudestMoment)}
                      </p>
                    </div>
                  )}
                  {preparationData.reflections.futureGoals && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>🎯 Future Goals</h4>
                      <p className={`text-sm ${mutedTextColor} whitespace-pre-wrap`}>
                        {cleanText(preparationData.reflections.futureGoals)}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
            
            {/* Talking Points */}
            {preparationData.talking_points && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`${sectionBgColor} rounded-lg p-4 border ${borderColor}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-green-500" />
                  <h3 className={`font-semibold ${textColor}`}>Key Talking Points</h3>
                </div>
                <div className="space-y-4">
                  {preparationData.talking_points.openingStatement && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>🎤 Opening Statement</h4>
                      <p className={`text-sm ${mutedTextColor} whitespace-pre-wrap`}>
                        {cleanText(preparationData.talking_points.openingStatement)}
                      </p>
                    </div>
                  )}
                  
                  {preparationData.talking_points.strengthHighlights && preparationData.talking_points.strengthHighlights.length > 0 && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>💪 Strength Highlights</h4>
                      <ul className={`text-sm ${mutedTextColor} space-y-1`}>
                        {preparationData.talking_points.strengthHighlights.map((highlight: string, idx: number) => (
                          <li key={idx} className="ml-4 list-disc">{cleanText(highlight)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {preparationData.talking_points.growthExamples && preparationData.talking_points.growthExamples.length > 0 && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>📈 Growth Examples</h4>
                      <ul className={`text-sm ${mutedTextColor} space-y-1`}>
                        {preparationData.talking_points.growthExamples.map((example: string, idx: number) => (
                          <li key={idx} className="ml-4 list-disc">{cleanText(example)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {preparationData.talking_points.challengeNarratives && preparationData.talking_points.challengeNarratives.length > 0 && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>🎯 Challenge Stories</h4>
                      <ul className={`text-sm ${mutedTextColor} space-y-1`}>
                        {preparationData.talking_points.challengeNarratives.map((narrative: string, idx: number) => (
                          <li key={idx} className="ml-4 list-disc">{cleanText(narrative)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {preparationData.talking_points.futureCommitments && preparationData.talking_points.futureCommitments.length > 0 && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>🚀 Future Commitments</h4>
                      <ul className={`text-sm ${mutedTextColor} space-y-1`}>
                        {preparationData.talking_points.futureCommitments.map((commitment: string, idx: number) => (
                          <li key={idx} className="ml-4 list-disc">{cleanText(commitment)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {preparationData.talking_points.questionsForManager && preparationData.talking_points.questionsForManager.length > 0 && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>❓ Questions for Manager</h4>
                      <ul className={`text-sm ${mutedTextColor} space-y-1`}>
                        {preparationData.talking_points.questionsForManager.map((question: string, idx: number) => (
                          <li key={idx} className="ml-4 list-disc">{cleanText(question)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {preparationData.talking_points.closingThoughts && (
                    <div>
                      <h4 className={`font-medium ${textColor} mb-1`}>🎬 Closing Thoughts</h4>
                      <p className={`text-sm ${mutedTextColor} whitespace-pre-wrap`}>
                        {cleanText(preparationData.talking_points.closingThoughts)}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
          
          {/* Footer */}
          <div className={`px-6 py-4 border-t ${borderColor} flex justify-end`}>
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-md ${
                darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              } transition-colors`}
            >
              Close
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default SavedPreparationViewer;